import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { ActivityLog, FeedbackStatus } from '../../../utility/feedback';
import { MatDialog } from '@angular/material/dialog';
import { DetailsDescriptionComponent } from '../details-description/details-description.component';
import { ContactCenterApiService } from '../../../v2/apis/contact-center/contact-center-api.service';
import { AssignTicketToDialogComponent } from '../assign-ticket-to-dialog/assign-ticket-to-dialog.component';
import { ViewChild } from '@angular/core';
import { FeedbackCommentTimelineComponent } from '../../feedback/feedback-comment-timeline/feedback-comment-timeline.component';
export interface RequestDetail {
    id: number;
    request_number: string;
    type: string;
    description: string;
    status: string;
    customer_name: string;
    assigned_to: string;
    created_date: string;
    updated_date?: string;
    given_by_first_name?: string;
    given_by_last_name?: string;
}

@Component({
    selector: 'app-request-detail',
    templateUrl: './request-detail.component.html',
    styleUrls: ['./request-detail.component.scss'],
    standalone: false
})

export class RequestDetailComponent implements OnInit {
    @ViewChild('timelineRef') timelineRef!: FeedbackCommentTimelineComponent;
    requestDetail: RequestDetail = {} as RequestDetail;
    historyDetails: ActivityLog[] = [];
    statusList: FeedbackStatus[] = [];
    selectedStatus: string = '';
    commentText: string = '';
    isSubmitting: boolean = false;
    isLoading: boolean = false;
    requestId: number = 0;
    headerData: any = {};
    uploadedFiles: File[] = [];
    private statusSubscription: Subscription | null = null;
    onFilesChanged(files: File[]): void {
        this.uploadedFiles = files;
    }

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private toastr: ToastrService,
        private dialog: MatDialog,
        private contactCenterService: ContactCenterApiService
    ) {
        const navigation = this.router.getCurrentNavigation();
        if (navigation?.extras?.state && navigation.extras.state['requestDetail']) {
            this.requestDetail = navigation.extras.state['requestDetail'];
        }
    }

    ngOnInit(): void {
        this.route.params.subscribe(params => {
            this.requestId = +params['id'];
            this.loadRequestWithLogs();
        });

        this.loadStatusList();
    }

    loadRequestWithLogs(): void {
        this.isLoading = true;
        this.contactCenterService.getRequestWithLogs(this.requestId).subscribe({
            next: (response) => {
                this.requestDetail = {
                    id: response.request.id,
                    request_number: `${response.request.id}`,
                    type: response.request.request_type,
                    description: response.request.description,
                    status: response.request.status,
                    customer_name: `${response.request.given_by_first_name} ${response.request.given_by_last_name}`,
                    assigned_to: 'Unassigned',
                    created_date: response.request.created_date,
                    updated_date: response.request.updated_date
                };

                this.selectedStatus = this.requestDetail.status;

                // header data
                this.headerData = {
                    submittedBy: `${response.request.given_by_first_name} ${response.request.given_by_last_name}`,
                    submittedOn: this.requestDetail.created_date,
                    assigned_category: response.request.assigned_category || 'Unassigned',
                    status: this.requestDetail.status,
                    showDetailsButton: true,
                    detailsButtonText: 'Details'
                };

                // Map activity logs and sort by date (newest first)
                this.historyDetails = response.activity_logs
                    .map(log => ({
                        id: log.id,
                        request_id: log.request_id,
                        wwid: log.given_by,
                        current_status: log.old_status || '',
                        new_status: log.new_status,
                        user_type: log.user_type,
                        assigned_from_category: (log as any).assigned_from_category || (log as any).assigned_from || '',
                        assigned_to_category: (log as any).assigned_to_category || '',
                        message: log.comments || log.description || `${log.activity_type}: Status changed to ${log.new_status}`,
                        created_at: log.created_date,
                        first_name: log.given_by_first_name,
                        last_name: log.given_by_last_name,
                        isUser: log.given_by === Number(sessionStorage.getItem('userId')),
                        attachment: (log as any).uploaded_file || (log as any).attachment || null,
                    }))
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error loading request details:', error);
                this.toastr.error('Failed to load request details.');
                this.isLoading = false;
            }
        });
    }
    loadStatusList(): void {
        this.isLoading = true;
        this.statusSubscription = this.contactCenterService.getRequestStatusMetadataList().subscribe({
            next: (response: any) => {
                if (Array.isArray(response)) {
                    this.statusList = response;
                } else if (response.data && Array.isArray(response.data)) {
                    this.statusList = response.data;
                } else {
                    this.statusList = [];
                    this.toastr.warning('No request statuses available.');
                }
                this.isLoading = false;
            },
            error: (error) => {
                console.error('Error loading status list:', error);
                this.toastr.error('Failed to load status list.');
                this.statusList = [];
                this.isLoading = false;
            }
        });
    }

    onStatusChange(selectedValue: string): void {
        this.selectedStatus = selectedValue;
    }

    addComment(): void {
        if (!this.selectedStatus) {
            this.toastr.error('Please select a status.');
            return;
        }
        if (this.selectedStatus === this.requestDetail.status && (!this.commentText || !this.commentText.trim())) {
            this.toastr.warning('Please select a different status or add a comment.');
            return;
        }
        this.isSubmitting = true;
        const payload = {
            request_id: this.requestId,
            new_status: this.selectedStatus,
            comments: this.commentText?.trim() || `Status changed from ${this.requestDetail.status} to ${this.selectedStatus}`,
            request_type: this.requestDetail.type === 'Bugs' ? 'Bugs' : 'Enhancements',
            given_by: Number(sessionStorage.getItem('userId')),
            activity_type: '',
        };

        const file = this.uploadedFiles.length > 0 ? this.uploadedFiles[0] : null;
        this.contactCenterService.updateRequestActivityLog(payload, file).subscribe({
            next: (response) => {
                this.toastr.success('Request updated successfully.');
                this.commentText = '';
                this.uploadedFiles = [];
                if (this.timelineRef) this.timelineRef.clearUploadedFiles();
                this.requestDetail.status = this.selectedStatus;
                this.loadRequestWithLogs();
                this.isSubmitting = false;
            },
            error: (error) => {
                console.error('Error updating request:', error);
                this.toastr.error('Failed to update request.');
                this.isSubmitting = false;
            }
        });
    }

    goBack(): void {
        this.router.navigate(['/app-request-list'], { skipLocationChange: true });
    }

    openAssignTicketToDialog(): void {
        const dialogRef = this.dialog.open(AssignTicketToDialogComponent, {
            width: '500px',
            data: {
                ticketId: this.requestId,
                currentAssignee: this.headerData?.assigned_category || '',
                referenceType: 'request'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.headerData = { ...this.headerData, assigned_category: result.assigned_category || 'Unassigned' };
                this.loadRequestWithLogs();
            }
        });
    }

    viewDetailsDescription(): void {
        const requestData = {
            id: this.requestDetail.id,
            request_number: this.requestDetail.request_number,
            customer_name: `${this.requestDetail.given_by_first_name} ${this.requestDetail.given_by_last_name}`,
            subject: this.requestDetail.description,
            status: this.requestDetail.status,
            assigned_to: this.requestDetail.assigned_to,
            created_date: new Date(this.requestDetail.created_date),
            updated_date: this.requestDetail.updated_date ? new Date(this.requestDetail.updated_date) : new Date(this.requestDetail.created_date)
        };

        this.dialog.open(DetailsDescriptionComponent, {
            width: '90vw',
            maxWidth: '1200px',
            height: '90vh',
            data: requestData,
            panelClass: 'details-description-dialog-container'
        });
    }
    ngOnDestroy(): void {
        if (this.statusSubscription) {
            this.statusSubscription.unsubscribe();
        }
    }
}
